import { getWmsProfile, resolveProfileKey } from '@/pages/wms/wms-profile-config';
import type { WmsProfile, WmsProfileKey } from '@/pages/wms/wms-profile-config';

export type WmsTemplateCode =
  | 'ecommerce-fulfillment'
  | 'general-3pl'
  | 'manufacturing-stores'
  | 'distribution-hub'
  | 'yard-control'
  | 'hybrid-flex';

export type WmsTemplateAssignmentScope =
  | 'tenant-default'
  | 'warehouse'
  | 'customer'
  | 'operation-type'
  | 'job-type';

export interface WmsRuntimeBundleStarter {
  code: string;
  label: string;
  semanticModelHint: string;
  workspaceTemplateHint: string;
  reportModelHint: string;
  billingModelHint: string;
}

export interface WmsProfileTemplate {
  code: WmsTemplateCode;
  scope: 'platform';
  profileKey: WmsProfileKey;
  label: string;
  summary: string;
  scenarioTags: string[];
  capabilityFlags: string[];
  supportedAssignmentScopes: WmsTemplateAssignmentScope[];
  defaultRuntimeBundle: WmsRuntimeBundleStarter;
}

export interface WmsTemplateAssignmentConfig {
  id: string;
  label: string;
  templateCode: WmsTemplateCode;
  priority?: number;
  enabled?: boolean;
  warehouseCode?: string;
  customerCode?: string;
  operationType?: string;
  jobType?: string;
}

export interface WmsTemplateResolutionContext {
  explicitTemplateCode?: string | null;
  warehouseCode?: string | null;
  customerCode?: string | null;
  operationType?: string | null;
  jobType?: string | null;
}

type WmsTemplateResolutionSearchParams = {
  get(name: string): string | null;
};

export interface WmsTemplateRuntimeConfig {
  warehouseProfile?: string | null;
  warehouseProfileTemplateCode?: string | null;
  warehouseTemplateAssignments?: WmsTemplateAssignmentConfig[] | null;
}

export interface WmsTemplateStarterPackRuntimeConfig extends WmsTemplateRuntimeConfig {
  warehouseProfile: WmsProfileKey;
  warehouseProfileTemplateCode: WmsTemplateCode;
  warehouseTemplateAssignments: WmsTemplateAssignmentConfig[];
}

export type WmsTemplateConfigPatch = WmsTemplateStarterPackRuntimeConfig;

export interface WmsTemplateStarterPack {
  code: WmsTemplateCode;
  label: string;
  narrative: string;
  runtimeConfig: WmsTemplateStarterPackRuntimeConfig;
}

export interface WmsTemplateComboPack {
  code: string;
  label: string;
  narrative: string;
  runtimeConfig: WmsTemplateStarterPackRuntimeConfig;
}

export type WmsFocusedTenantStarterCode =
  | 'wasco-malaysia'
  | 'revochem'
  | 'nek'
  | 'new-revo';

export interface WmsFocusedTenantValidationContext extends WmsTemplateResolutionContext {
  label: string;
  expectedTemplateCode: WmsTemplateCode;
}

export interface WmsFocusedTenantUatSmokeStep {
  id: string;
  label: string;
  route: string;
  context: WmsTemplateResolutionContext;
  expectedTemplateCode: WmsTemplateCode;
}

export interface WmsFocusedTenantStarterConfig {
  code: WmsFocusedTenantStarterCode;
  accountName: string;
  focusProfile: 'manufacturing' | 'generic' | 'yard-control';
  templateCode: WmsTemplateCode;
  narrative: string;
  runtimeConfig: WmsTemplateStarterPackRuntimeConfig;
  validationContexts: WmsFocusedTenantValidationContext[];
  uatSmokePath: WmsFocusedTenantUatSmokeStep[];
}

export interface WmsFocusedTenantUatSmokeStepResult extends WmsFocusedTenantUatSmokeStep {
  resolvedTemplateCode: WmsTemplateCode;
  resolvedProfileKey: WmsProfileKey;
  source: ResolvedWmsTemplateSelection['source'];
  matchedAssignmentId: string | null;
  ready: boolean;
}

export interface WmsFocusedTenantUatSmokeMatrixRow {
  code: WmsFocusedTenantStarterCode;
  accountName: string;
  focusProfile: WmsFocusedTenantStarterConfig['focusProfile'];
  templateCode: WmsTemplateCode;
  readyCount: number;
  totalCount: number;
  steps: WmsFocusedTenantUatSmokeStepResult[];
}

export type WmsRuntimeArtifactKind =
  | 'semantic-model'
  | 'workspace-template'
  | 'report-model'
  | 'billing-model';

export type WmsActiveProfileTemplateCode =
  | 'manufacturing-stores'
  | 'general-3pl'
  | 'yard-control';

export interface WmsTemplateCapabilityMatrixRow {
  templateCode: WmsTemplateCode;
  profileKey: WmsProfileKey;
  label: string;
  scenarioTags: string[];
  capabilityFlags: string[];
  supportedAssignmentScopes: WmsTemplateAssignmentScope[];
  runtimeBundleCode: string;
  runtimeBundleLabel: string;
  runtimeArtifacts: Record<WmsRuntimeArtifactKind, string>;
  dashboardKpis: number;
  processSteps: number;
  quickLinks: number;
  navMode: 'all' | 'gated';
}

export interface WmsRuntimeArtifactBuilderParams {
  artifactType: WmsRuntimeArtifactKind;
  publishName: string;
  description: string;
}

export interface WmsRuntimeArtifactPublishedItem {
  slug: string;
  name?: string | null;
}

export interface WmsRuntimeArtifactReadinessItem {
  kind: WmsRuntimeArtifactKind;
  label: string;
  expectedSlug: string;
  status: 'published' | 'missing';
  publishedName: string | null;
}

export interface WmsTemplateRuntimeArtifactReadiness {
  templateCode: WmsActiveProfileTemplateCode;
  templateLabel: string;
  profileKey: WmsProfileKey;
  readyCount: number;
  totalCount: number;
  artifacts: WmsRuntimeArtifactReadinessItem[];
}

export interface WmsTemplateConfigReadinessCheck {
  key: string;
  label: string;
  ready: boolean;
  detail: string;
}

export interface WmsRuntimePageContext {
  pageKey: string;
  templateCode: WmsTemplateCode;
  profileKey: WmsProfileKey;
  templateLabel: string;
  source: ResolvedWmsTemplateSelection['source'];
  bundleCode: string;
  semanticModelHint: string;
  workspaceTemplateHint: string;
  reportModelHint: string;
  billingModelHint: string;
  matchedAssignmentLabel: string | null;
}

export interface WmsRuntimeWorkspaceTab {
  key: string;
  label: string;
}

export interface WmsRuntimeColumn {
  field: string;
  label: string;
}

export interface WmsRuntimeColumnTab extends WmsRuntimeWorkspaceTab {
  columns: WmsRuntimeColumn[];
}

export interface WmsRuntimeWorkspaceTemplate {
  name?: string;
  mainPanel?: {
    title?: string;
    defaultBoardId?: string;
  };
  boards?: Array<{
    id: string;
    label: string;
    sections?: Array<{
      id: string;
      label: string;
      content?: {
        entitySlug?: string;
        rendererKey?: string;
        resourcePath?: string;
        bindingId?: string;
      };
    }>;
  }>;
  queues?: Array<{
    id: string;
    label: string;
    bindingId?: string;
    content?: {
      entitySlug?: string;
      rendererKey?: string;
      resourcePath?: string;
      bindingId?: string;
    };
  }>;
}

export interface WmsWorkspaceTabOverlayResult<TTab extends WmsRuntimeWorkspaceTab> {
  tabs: TTab[];
  applied: boolean;
  appliedLabels: string[];
}

export interface WmsRuntimeReportModel {
  name?: string;
  dimensions?: Array<{ id: string; label: string; field: string }>;
  measures?: Array<{ id: string; label: string; field: string }>;
}

export interface WmsRuntimeBillingModel {
  name?: string;
  chargeFamilies?: Array<{ id: string; code: string; label: string; billableUnit?: string }>;
  ratingRules?: Array<{ id: string; familyId: string; rateSource: string; rateKey?: string }>;
}

export interface WmsColumnOverlayResult<TTab extends WmsRuntimeColumnTab> {
  tabs: TTab[];
  applied: boolean;
  appliedLabels: string[];
}

export interface ResolvedWmsTemplateSelection {
  templateCode: WmsTemplateCode;
  template: WmsProfileTemplate;
  profileKey: WmsProfileKey;
  profile: WmsProfile;
  source: 'explicit' | 'assignment' | 'template-code' | 'profile-fallback';
  matchedAssignment: WmsTemplateAssignmentConfig | null;
  assignments: WmsTemplateAssignmentConfig[];
}

const PROFILE_TO_TEMPLATE_CODE: Partial<Record<WmsProfileKey, WmsTemplateCode>> = {
  ecommerce: 'ecommerce-fulfillment',
  general: 'general-3pl',
  manufacturing: 'manufacturing-stores',
  distribution: 'distribution-hub',
  yard: 'yard-control',
  hybrid: 'hybrid-flex',
};

export const PLATFORM_WMS_TEMPLATE_RESOLUTION_ORDER = [
  '1. 显式任务 / 工作流指定模板',
  '2. 客户 + 仓库联合 assignment',
  '3. 仓库 assignment',
  '4. 客户 assignment',
  '5. 租户默认模板',
  '6. 旧版 warehouseProfile 回退',
] as const;

export const WMS_PROFILE_TEMPLATES: Record<WmsTemplateCode, WmsProfileTemplate> = {
  'ecommerce-fulfillment': {
    code: 'ecommerce-fulfillment',
    scope: 'platform',
    profileKey: 'ecommerce',
    label: 'Ecommerce Fulfillment',
    summary: '面向 B2C 履约、波次拣货、包装台和高频出货的电商模板。',
    scenarioTags: ['B2C', 'Fulfillment', 'Pick-Pack', 'Last Mile'],
    capabilityFlags: ['order-burst', 'wave-picking', 'parcel-packout', 'returns-processing'],
    supportedAssignmentScopes: ['tenant-default', 'warehouse', 'customer', 'operation-type', 'job-type'],
    defaultRuntimeBundle: {
      code: 'bundle-ecommerce-fulfillment',
      label: 'Ecommerce Starter Bundle',
      semanticModelHint: 'wms-ecommerce-semantic-v1',
      workspaceTemplateHint: 'workspace-ecommerce-fulfillment-v1',
      reportModelHint: 'report-ecommerce-sla-v1',
      billingModelHint: 'billing-ecommerce-activity-v1',
    },
  },
  'general-3pl': {
    code: 'general-3pl',
    scope: 'platform',
    profileKey: 'general',
    label: 'Generic Warehouse',
    summary: '适用于 Revochem / NEK 这类通用仓储，兼顾收货、上架、库存、出货与基础计费。',
    scenarioTags: ['Generic', 'Multi-customer', 'Shared Warehouse'],
    capabilityFlags: ['balanced-ops', 'shared-inventory', 'basic-billing', 'customer-flexibility'],
    supportedAssignmentScopes: ['tenant-default', 'warehouse', 'customer', 'operation-type'],
    defaultRuntimeBundle: {
      code: 'bundle-general-3pl',
      label: 'General 3PL Bundle',
      semanticModelHint: 'wms-general-semantic-v1',
      workspaceTemplateHint: 'workspace-general-warehouse-v1',
      reportModelHint: 'report-general-operations-v1',
      billingModelHint: 'billing-general-warehouse-v1',
    },
  },
  'manufacturing-stores': {
    code: 'manufacturing-stores',
    scope: 'platform',
    profileKey: 'manufacturing',
    label: 'Manufacturing Stores',
    summary: '服务生产仓、线边仓、物料补给和半成品流转的模板。',
    scenarioTags: ['Manufacturing', 'Line-side', 'Kitting', 'Raw Material'],
    capabilityFlags: ['material-staging', 'kit-assembly', 'work-order-support', 'quality-gate'],
    supportedAssignmentScopes: ['tenant-default', 'warehouse', 'customer', 'job-type'],
    defaultRuntimeBundle: {
      code: 'bundle-manufacturing-stores',
      label: 'Manufacturing Starter Bundle',
      semanticModelHint: 'wms-manufacturing-semantic-v1',
      workspaceTemplateHint: 'workspace-manufacturing-ops-v1',
      reportModelHint: 'report-manufacturing-replenishment-v1',
      billingModelHint: 'billing-manufacturing-services-v1',
    },
  },
  'distribution-hub': {
    code: 'distribution-hub',
    scope: 'platform',
    profileKey: 'distribution',
    label: 'Distribution Hub',
    summary: '适合区域配送中心、批量收发、交叉转运与门店补货场景。',
    scenarioTags: ['Distribution', 'B2B', 'Cross Dock', 'Replenishment'],
    capabilityFlags: ['dock-door-flow', 'cross-dock', 'store-replenishment', 'route-batching'],
    supportedAssignmentScopes: ['tenant-default', 'warehouse', 'customer', 'operation-type', 'job-type'],
    defaultRuntimeBundle: {
      code: 'bundle-distribution-hub',
      label: 'Distribution Hub Bundle',
      semanticModelHint: 'wms-distribution-semantic-v1',
      workspaceTemplateHint: 'workspace-distribution-hub-v1',
      reportModelHint: 'report-distribution-throughput-v1',
      billingModelHint: 'billing-distribution-services-v1',
    },
  },
  'yard-control': {
    code: 'yard-control',
    scope: 'platform',
    profileKey: 'yard',
    label: 'Yard Control',
    summary: '聚焦空重柜、拖车排队、堆场位置与进出场管控的 yard 模板。',
    scenarioTags: ['Yard', 'Container', 'Gate In/Out', 'Staging'],
    capabilityFlags: ['yard-visibility', 'gate-control', 'slot-management', 'equipment-tracking'],
    supportedAssignmentScopes: ['tenant-default', 'warehouse', 'operation-type'],
    defaultRuntimeBundle: {
      code: 'bundle-yard-control',
      label: 'Yard Starter Bundle',
      semanticModelHint: 'wms-yard-semantic-v1',
      workspaceTemplateHint: 'workspace-yard-control-v1',
      reportModelHint: 'report-yard-movements-v1',
      billingModelHint: 'billing-yard-services-v1',
    },
  },
  'hybrid-flex': {
    code: 'hybrid-flex',
    scope: 'platform',
    profileKey: 'hybrid',
    label: 'Hybrid Flex',
    summary: '用于混合场景的全量模板，作为多业务仓或迁移阶段的安全回退。',
    scenarioTags: ['Hybrid', 'Multi-scenario', 'Migration'],
    capabilityFlags: ['full-nav', 'broad-kpis', 'mixed-operations', 'safe-fallback'],
    supportedAssignmentScopes: ['tenant-default', 'warehouse', 'customer', 'operation-type', 'job-type'],
    defaultRuntimeBundle: {
      code: 'bundle-hybrid-flex',
      label: 'Hybrid Flex Bundle',
      semanticModelHint: 'wms-hybrid-semantic-v1',
      workspaceTemplateHint: 'workspace-hybrid-flex-v1',
      reportModelHint: 'report-hybrid-operations-v1',
      billingModelHint: 'billing-hybrid-services-v1',
    },
  },
};

export const WMS_PROFILE_TEMPLATE_LIST = Object.values(WMS_PROFILE_TEMPLATES);

export const WMS_RUNTIME_ARTIFACT_KINDS: WmsRuntimeArtifactKind[] = [
  'semantic-model',
  'workspace-template',
  'report-model',
  'billing-model',
];

export const WMS_ACTIVE_PROFILE_TEMPLATE_CODES: WmsActiveProfileTemplateCode[] = [
  'manufacturing-stores',
  'general-3pl',
  'yard-control',
];

const WMS_RUNTIME_ARTIFACT_LABELS: Record<WmsRuntimeArtifactKind, string> = {
  'semantic-model': 'Semantic Model',
  'workspace-template': 'Workspace Template',
  'report-model': 'Report Model',
  'billing-model': 'Billing Model',
};

export function buildWmsTemplateCapabilityMatrix(): WmsTemplateCapabilityMatrixRow[] {
  return WMS_PROFILE_TEMPLATE_LIST.map((template) => {
    const profile = getWmsProfile(template.profileKey);
    const bundle = template.defaultRuntimeBundle;

    return {
      templateCode: template.code,
      profileKey: template.profileKey,
      label: template.label,
      scenarioTags: template.scenarioTags,
      capabilityFlags: template.capabilityFlags,
      supportedAssignmentScopes: template.supportedAssignmentScopes,
      runtimeBundleCode: bundle.code,
      runtimeBundleLabel: bundle.label,
      runtimeArtifacts: {
        'semantic-model': bundle.semanticModelHint,
        'workspace-template': bundle.workspaceTemplateHint,
        'report-model': bundle.reportModelHint,
        'billing-model': bundle.billingModelHint,
      },
      dashboardKpis: profile.kpis.length,
      processSteps: profile.flowSteps.length,
      quickLinks: profile.quickLinks.length,
      navMode: profile.visibleNavIds ? 'gated' : 'all',
    };
  });
}

export function buildWmsRuntimeArtifactBuilderParams(
  row: WmsTemplateCapabilityMatrixRow,
  artifactType: WmsRuntimeArtifactKind,
): WmsRuntimeArtifactBuilderParams {
  const publishName = row.runtimeArtifacts[artifactType];
  const scenario = row.scenarioTags.join(', ');
  const capabilities = row.capabilityFlags.join(', ');
  const scopes = row.supportedAssignmentScopes.join(', ');

  return {
    artifactType,
    publishName,
    description: [
      `Create a WMS ${artifactType} for the ${row.label} template.`,
      `Profile: ${row.profileKey}.`,
      `Runtime bundle: ${row.runtimeBundleLabel} (${row.runtimeBundleCode}).`,
      `Scenario tags: ${scenario}.`,
      `Capability flags: ${capabilities}.`,
      `Supported assignment scopes: ${scopes}.`,
      `Use publish name ${publishName}.`,
    ].join(' '),
  };
}

export function buildWmsRuntimeArtifactBuilderUrl(
  row: WmsTemplateCapabilityMatrixRow,
  artifactType: WmsRuntimeArtifactKind,
): string {
  const params = buildWmsRuntimeArtifactBuilderParams(row, artifactType);
  const search = new URLSearchParams({
    artifactType: params.artifactType,
    publishName: params.publishName,
    description: params.description,
  });

  return `/admin/builder?${search.toString()}`;
}

export function buildWmsActiveProfileRuntimeArtifactReadiness(
  publishedArtifacts: Partial<Record<WmsRuntimeArtifactKind, WmsRuntimeArtifactPublishedItem[]>>,
  templateCodes: WmsActiveProfileTemplateCode[] = WMS_ACTIVE_PROFILE_TEMPLATE_CODES,
): WmsTemplateRuntimeArtifactReadiness[] {
  return templateCodes.map((templateCode) => {
    const template = WMS_PROFILE_TEMPLATES[templateCode];
    const bundle = template.defaultRuntimeBundle;
    const expectedByKind: Record<WmsRuntimeArtifactKind, string> = {
      'semantic-model': bundle.semanticModelHint,
      'workspace-template': bundle.workspaceTemplateHint,
      'report-model': bundle.reportModelHint,
      'billing-model': bundle.billingModelHint,
    };
    const artifacts = WMS_RUNTIME_ARTIFACT_KINDS.map((kind) => {
      const expectedSlug = expectedByKind[kind];
      const published = (publishedArtifacts[kind] ?? []).find((item) => item.slug === expectedSlug) ?? null;
      const status: WmsRuntimeArtifactReadinessItem['status'] = published ? 'published' : 'missing';
      return {
        kind,
        label: WMS_RUNTIME_ARTIFACT_LABELS[kind],
        expectedSlug,
        status,
        publishedName: published?.name ?? null,
      };
    });

    return {
      templateCode,
      templateLabel: template.label,
      profileKey: template.profileKey,
      readyCount: artifacts.filter((item) => item.status === 'published').length,
      totalCount: artifacts.length,
      artifacts,
    };
  });
}

export function buildWmsTemplateConfigReadiness(
  config: WmsTemplateRuntimeConfig,
): WmsTemplateConfigReadinessCheck[] {
  const resolved = resolveWmsTemplateSelection(config);
  const bundle = resolved.template.defaultRuntimeBundle;
  const assignments = resolved.assignments;

  return [
    {
      key: 'profile',
      label: 'Active profile',
      ready: Boolean(config.warehouseProfile),
      detail: config.warehouseProfile
        ? `${resolved.profile.label} (${resolved.profileKey})`
        : `Fallback profile: ${resolved.profile.label} (${resolved.profileKey})`,
    },
    {
      key: 'default-template',
      label: 'Default template code',
      ready: isWmsTemplateCode(config.warehouseProfileTemplateCode),
      detail: isWmsTemplateCode(config.warehouseProfileTemplateCode)
        ? `${resolved.template.label} (${resolved.templateCode})`
        : `Resolved by profile fallback: ${resolved.template.label} (${resolved.templateCode})`,
    },
    {
      key: 'assignment-rules',
      label: 'Assignment rules',
      ready: assignments.length > 0,
      detail: assignments.length > 0
        ? `${assignments.length} enabled rule${assignments.length === 1 ? '' : 's'}`
        : 'No assignment rules configured',
    },
    {
      key: 'semantic-model',
      label: 'Semantic model hint',
      ready: Boolean(bundle.semanticModelHint),
      detail: bundle.semanticModelHint,
    },
    {
      key: 'workspace-template',
      label: 'Workspace template hint',
      ready: Boolean(bundle.workspaceTemplateHint),
      detail: bundle.workspaceTemplateHint,
    },
    {
      key: 'report-model',
      label: 'Report model hint',
      ready: Boolean(bundle.reportModelHint),
      detail: bundle.reportModelHint,
    },
    {
      key: 'billing-model',
      label: 'Billing model hint',
      ready: Boolean(bundle.billingModelHint),
      detail: bundle.billingModelHint,
    },
  ];
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readOptionalInteger(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new Error('priority must be an integer when provided');
  }
  return value;
}

export function normalizeWmsTemplateAssignmentsInput(value: unknown): WmsTemplateAssignmentConfig[] {
  if (!Array.isArray(value)) {
    throw new Error('warehouseTemplateAssignments must be a JSON array');
  }

  return value.map((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new Error(`assignment[${index}] must be an object`);
    }

    const record = item as Record<string, unknown>;
    const id = readOptionalString(record.id);
    const label = readOptionalString(record.label);
    const templateCode = readOptionalString(record.templateCode);

    if (!id) throw new Error(`assignment[${index}].id is required`);
    if (!label) throw new Error(`assignment[${index}].label is required`);
    if (!isWmsTemplateCode(templateCode)) {
      throw new Error(`assignment[${index}].templateCode is not a supported WMS template`);
    }
    if (record.enabled !== undefined && typeof record.enabled !== 'boolean') {
      throw new Error(`assignment[${index}].enabled must be boolean when provided`);
    }

    return {
      id,
      label,
      templateCode,
      priority: readOptionalInteger(record.priority),
      enabled: typeof record.enabled === 'boolean' ? record.enabled : undefined,
      warehouseCode: readOptionalString(record.warehouseCode),
      customerCode: readOptionalString(record.customerCode),
      operationType: readOptionalString(record.operationType),
      jobType: readOptionalString(record.jobType),
    };
  });
}

export function buildWmsTemplateConfigPatch(
  templateCode: WmsTemplateCode,
  assignments: WmsTemplateAssignmentConfig[],
): WmsTemplateConfigPatch {
  const template = WMS_PROFILE_TEMPLATES[templateCode];

  return {
    warehouseProfile: template.profileKey,
    warehouseProfileTemplateCode: templateCode,
    warehouseTemplateAssignments: sortWmsTemplateAssignments(assignments),
  };
}

function buildStarterPack(
  templateCode: WmsTemplateCode,
  label: string,
  narrative: string,
  warehouseTemplateAssignments: WmsTemplateAssignmentConfig[],
): WmsTemplateStarterPack {
  const template = WMS_PROFILE_TEMPLATES[templateCode];
  return {
    code: templateCode,
    label,
    narrative,
    runtimeConfig: {
      warehouseProfile: template.profileKey,
      warehouseProfileTemplateCode: templateCode,
      warehouseTemplateAssignments,
    },
  };
}

export const WMS_TEMPLATE_STARTER_PACKS: WmsTemplateStarterPack[] = [
  buildStarterPack(
    'ecommerce-fulfillment',
    'Ecommerce Starter Pack',
    '适合高单量 B2C 履约、波次拣货、包裹出货与退货处理。',
    [
      { id: 'ecom-default', label: 'Ecommerce Default', templateCode: 'ecommerce-fulfillment', priority: 10 },
      {
        id: 'ecom-b2c-wave',
        label: 'B2C Wave Picking',
        templateCode: 'ecommerce-fulfillment',
        priority: 90,
        warehouseCode: 'ECOM-FC-01',
        jobType: 'sales-order',
      },
    ],
  ),
  buildStarterPack(
    'general-3pl',
    'Generic Warehouse Starter Pack',
    '适合 Revochem / NEK 的通用仓储、多客户第三方仓储与基础增值服务场景。',
    [
      { id: 'general-default', label: 'General Default', templateCode: 'general-3pl', priority: 10 },
      {
        id: 'general-shared-wh',
        label: 'Shared Warehouse',
        templateCode: 'general-3pl',
        priority: 70,
        warehouseCode: 'GEN-WH-01',
      },
    ],
  ),
  buildStarterPack(
    'manufacturing-stores',
    'Manufacturing Starter Pack',
    '适合生产仓、线边仓、工单补料、工序退料与物料 staging。',
    [
      { id: 'mfg-default', label: 'Manufacturing Default', templateCode: 'manufacturing-stores', priority: 10 },
      {
        id: 'mfg-work-order',
        label: 'Work Order Supply',
        templateCode: 'manufacturing-stores',
        priority: 90,
        warehouseCode: 'PLANT-STORE-01',
        jobType: 'work-order',
      },
    ],
  ),
  buildStarterPack(
    'distribution-hub',
    'Distribution Starter Pack',
    '适合配送中心、门店补货、批次集货与 cross-dock 调度。',
    [
      { id: 'dist-default', label: 'Distribution Default', templateCode: 'distribution-hub', priority: 10 },
      {
        id: 'dist-replenishment',
        label: 'Store Replenishment',
        templateCode: 'distribution-hub',
        priority: 85,
        warehouseCode: 'DC-SG-01',
        jobType: 'store-replenishment',
      },
    ],
  ),
  buildStarterPack(
    'yard-control',
    'Yard Starter Pack',
    '适合堆场、拖车排队、空重柜切换、闸口与 slot 管控。',
    [
      { id: 'yard-default', label: 'Yard Default', templateCode: 'yard-control', priority: 10 },
      {
        id: 'yard-gate',
        label: 'Gate Move',
        templateCode: 'yard-control',
        priority: 90,
        warehouseCode: 'YARD-01',
        operationType: 'gate-move',
      },
    ],
  ),
  buildStarterPack(
    'hybrid-flex',
    'Hybrid Starter Pack',
    '适合混合业务仓、过渡期租户和需要全量菜单回退的场景。',
    [
      { id: 'hybrid-default', label: 'Hybrid Default', templateCode: 'hybrid-flex', priority: 10 },
      {
        id: 'hybrid-overflow',
        label: 'Overflow Operation',
        templateCode: 'hybrid-flex',
        priority: 75,
        warehouseCode: 'HYB-OPS-01',
        operationType: 'overflow',
      },
    ],
  ),
];

export const WMS_TEMPLATE_STARTER_PACK_INDEX: Record<WmsTemplateCode, WmsTemplateStarterPack> =
  Object.fromEntries(WMS_TEMPLATE_STARTER_PACKS.map((pack) => [pack.code, pack])) as Record<WmsTemplateCode, WmsTemplateStarterPack>;

export const WMS_TEMPLATE_COMBO_PACKS: WmsTemplateComboPack[] = [
  {
    code: 'shared-campus',
    label: 'Shared Campus Combo',
    narrative: '一个租户同时运行 general、ecommerce 与 distribution，按仓库和客户自动切换。',
    runtimeConfig: {
      warehouseProfile: 'general',
      warehouseProfileTemplateCode: 'general-3pl',
      warehouseTemplateAssignments: [
        { id: 'campus-default', label: 'Campus Default', templateCode: 'general-3pl', priority: 10 },
        {
          id: 'campus-ecom-wh',
          label: 'Ecommerce FC',
          templateCode: 'ecommerce-fulfillment',
          priority: 90,
          warehouseCode: 'SG-ECOM-01',
        },
        {
          id: 'campus-b2b-dc',
          label: 'B2B Distribution',
          templateCode: 'distribution-hub',
          priority: 85,
          warehouseCode: 'SG-DC-01',
          jobType: 'store-replenishment',
        },
        {
          id: 'campus-vip-customer',
          label: 'VIP Ecommerce Customer',
          templateCode: 'ecommerce-fulfillment',
          priority: 95,
          warehouseCode: 'SG-ECOM-01',
          customerCode: 'VIP-ECOM',
        },
      ],
    },
  },
  {
    code: 'factory-plus-yard',
    label: 'Factory + Yard Combo',
    narrative: '同一客户既有生产物料仓，又有厂区 yard，按仓区与作业类型切换。',
    runtimeConfig: {
      warehouseProfile: 'manufacturing',
      warehouseProfileTemplateCode: 'manufacturing-stores',
      warehouseTemplateAssignments: [
        { id: 'factory-default', label: 'Factory Default', templateCode: 'manufacturing-stores', priority: 10 },
        {
          id: 'factory-yard-gate',
          label: 'Factory Yard Gate',
          templateCode: 'yard-control',
          priority: 92,
          warehouseCode: 'PLANT-YARD-01',
          operationType: 'gate-move',
        },
        {
          id: 'factory-linefeed',
          label: 'Line Feed',
          templateCode: 'manufacturing-stores',
          priority: 88,
          warehouseCode: 'LINE-STORE-01',
          jobType: 'work-order',
        },
      ],
    },
  },
];

export const WMS_FOCUSED_TENANT_STARTER_CONFIGS: WmsFocusedTenantStarterConfig[] = [
  {
    code: 'wasco-malaysia',
    accountName: 'Wasco Malaysia',
    focusProfile: 'manufacturing',
    templateCode: 'manufacturing-stores',
    narrative: 'Wasco Malaysia uses the manufacturing profile for plant store, line store, work-order supply, QC and project-tracked material movement.',
    runtimeConfig: {
      warehouseProfile: 'manufacturing',
      warehouseProfileTemplateCode: 'manufacturing-stores',
      warehouseTemplateAssignments: [
        { id: 'wasco-default', label: 'Wasco Manufacturing Default', templateCode: 'manufacturing-stores', priority: 10 },
        {
          id: 'wasco-plant-store',
          label: 'Wasco Plant Store',
          templateCode: 'manufacturing-stores',
          priority: 90,
          warehouseCode: 'WH-WASCO-MY',
        },
        {
          id: 'wasco-line-store',
          label: 'Wasco Line Store',
          templateCode: 'manufacturing-stores',
          priority: 88,
          warehouseCode: 'LINE-STORE-01',
          jobType: 'work-order',
        },
        {
          id: 'wasco-work-order-supply',
          label: 'Wasco Work Order Supply',
          templateCode: 'manufacturing-stores',
          priority: 80,
          jobType: 'work-order',
        },
      ],
    },
    validationContexts: [
      { label: 'Plant store', warehouseCode: 'WH-WASCO-MY', expectedTemplateCode: 'manufacturing-stores' },
      { label: 'Line store work order', warehouseCode: 'LINE-STORE-01', jobType: 'work-order', expectedTemplateCode: 'manufacturing-stores' },
      { label: 'Work order supply', jobType: 'work-order', expectedTemplateCode: 'manufacturing-stores' },
    ],
    uatSmokePath: [
      {
        id: 'wasco-po-upload',
        label: 'PO upload',
        route: '/wms/purchase-orders?warehouseCode=WH-WASCO-MY',
        context: { warehouseCode: 'WH-WASCO-MY' },
        expectedTemplateCode: 'manufacturing-stores',
      },
      {
        id: 'wasco-receive-qc',
        label: 'Receive and QC',
        route: '/wms/inbound?warehouseCode=WH-WASCO-MY',
        context: { warehouseCode: 'WH-WASCO-MY' },
        expectedTemplateCode: 'manufacturing-stores',
      },
      {
        id: 'wasco-putaway',
        label: 'Putaway',
        route: '/wms/inventory?warehouseCode=WH-WASCO-MY',
        context: { warehouseCode: 'WH-WASCO-MY' },
        expectedTemplateCode: 'manufacturing-stores',
      },
      {
        id: 'wasco-issue-return',
        label: 'Issue or return to project',
        route: '/wms/material-requests?jobType=work-order',
        context: { jobType: 'work-order' },
        expectedTemplateCode: 'manufacturing-stores',
      },
      {
        id: 'wasco-inventory-ledger',
        label: 'Inventory ledger',
        route: '/wms/stock-ledger?warehouseCode=WH-WASCO-MY',
        context: { warehouseCode: 'WH-WASCO-MY' },
        expectedTemplateCode: 'manufacturing-stores',
      },
    ],
  },
  {
    code: 'revochem',
    accountName: 'Revochem',
    focusProfile: 'generic',
    templateCode: 'general-3pl',
    narrative: 'Revochem uses the Generic Warehouse profile for shared warehouse operations, standard receiving, dispatch and stock reporting.',
    runtimeConfig: {
      warehouseProfile: 'general',
      warehouseProfileTemplateCode: 'general-3pl',
      warehouseTemplateAssignments: [
        { id: 'revochem-default', label: 'Revochem Generic Default', templateCode: 'general-3pl', priority: 10 },
        {
          id: 'revochem-shared-wh',
          label: 'Revochem Shared Warehouse',
          templateCode: 'general-3pl',
          priority: 85,
          warehouseCode: 'WH-REVO-SG-01',
        },
        {
          id: 'revochem-standard-receive',
          label: 'Revochem Standard Receive',
          templateCode: 'general-3pl',
          priority: 75,
          operationType: 'standard-receive',
        },
        {
          id: 'revochem-standard-dispatch',
          label: 'Revochem Standard Dispatch',
          templateCode: 'general-3pl',
          priority: 70,
          operationType: 'standard-dispatch',
        },
      ],
    },
    validationContexts: [
      { label: 'Shared warehouse', warehouseCode: 'WH-REVO-SG-01', expectedTemplateCode: 'general-3pl' },
      { label: 'Standard receive', operationType: 'standard-receive', expectedTemplateCode: 'general-3pl' },
      { label: 'Standard dispatch', operationType: 'standard-dispatch', expectedTemplateCode: 'general-3pl' },
    ],
    uatSmokePath: [
      {
        id: 'revochem-receive',
        label: 'Receive',
        route: '/wms/inbound?warehouseCode=WH-REVO-SG-01&operationType=standard-receive',
        context: { warehouseCode: 'WH-REVO-SG-01', operationType: 'standard-receive' },
        expectedTemplateCode: 'general-3pl',
      },
      {
        id: 'revochem-inventory',
        label: 'Inventory',
        route: '/wms/inventory?warehouseCode=WH-REVO-SG-01',
        context: { warehouseCode: 'WH-REVO-SG-01' },
        expectedTemplateCode: 'general-3pl',
      },
      {
        id: 'revochem-outbound',
        label: 'Outbound',
        route: '/wms/outbound?operationType=standard-dispatch',
        context: { operationType: 'standard-dispatch' },
        expectedTemplateCode: 'general-3pl',
      },
      {
        id: 'revochem-stock-take',
        label: 'Stock take',
        route: '/wms/stock-take?warehouseCode=WH-REVO-SG-01',
        context: { warehouseCode: 'WH-REVO-SG-01' },
        expectedTemplateCode: 'general-3pl',
      },
      {
        id: 'revochem-report',
        label: 'Report',
        route: '/wms/reports?warehouseCode=WH-REVO-SG-01',
        context: { warehouseCode: 'WH-REVO-SG-01' },
        expectedTemplateCode: 'general-3pl',
      },
    ],
  },
  {
    code: 'nek',
    accountName: 'NEK',
    focusProfile: 'generic',
    templateCode: 'general-3pl',
    narrative: 'NEK uses the Generic Warehouse profile for inventory visibility, basic inbound/outbound movement and warehouse billing readiness.',
    runtimeConfig: {
      warehouseProfile: 'general',
      warehouseProfileTemplateCode: 'general-3pl',
      warehouseTemplateAssignments: [
        { id: 'nek-default', label: 'NEK Generic Default', templateCode: 'general-3pl', priority: 10 },
        {
          id: 'nek-inventory-visibility',
          label: 'NEK Inventory Visibility',
          templateCode: 'general-3pl',
          priority: 85,
          warehouseCode: 'NEK-WH-01',
        },
        {
          id: 'nek-basic-billing',
          label: 'NEK Basic Warehouse Billing',
          templateCode: 'general-3pl',
          priority: 75,
          operationType: 'storage-billing',
        },
      ],
    },
    validationContexts: [
      { label: 'Inventory visibility', warehouseCode: 'NEK-WH-01', expectedTemplateCode: 'general-3pl' },
      { label: 'Basic billing', operationType: 'storage-billing', expectedTemplateCode: 'general-3pl' },
    ],
    uatSmokePath: [
      {
        id: 'nek-inventory-visibility',
        label: 'Inventory visibility',
        route: '/wms/inventory?warehouseCode=NEK-WH-01',
        context: { warehouseCode: 'NEK-WH-01' },
        expectedTemplateCode: 'general-3pl',
      },
      {
        id: 'nek-inbound',
        label: 'Inbound',
        route: '/wms/inbound?warehouseCode=NEK-WH-01',
        context: { warehouseCode: 'NEK-WH-01' },
        expectedTemplateCode: 'general-3pl',
      },
      {
        id: 'nek-outbound',
        label: 'Outbound',
        route: '/wms/outbound?warehouseCode=NEK-WH-01',
        context: { warehouseCode: 'NEK-WH-01' },
        expectedTemplateCode: 'general-3pl',
      },
      {
        id: 'nek-basic-report',
        label: 'Basic warehouse report',
        route: '/wms/reports?operationType=storage-billing',
        context: { operationType: 'storage-billing' },
        expectedTemplateCode: 'general-3pl',
      },
    ],
  },
  {
    code: 'new-revo',
    accountName: 'New Revo',
    focusProfile: 'yard-control',
    templateCode: 'yard-control',
    narrative: 'New Revo uses the Yard Control profile for gate moves, yard staging and slot control around container and cargo yard operations.',
    runtimeConfig: {
      warehouseProfile: 'yard',
      warehouseProfileTemplateCode: 'yard-control',
      warehouseTemplateAssignments: [
        { id: 'new-revo-default', label: 'New Revo Yard Default', templateCode: 'yard-control', priority: 10 },
        {
          id: 'new-revo-gate-move',
          label: 'New Revo Gate Move',
          templateCode: 'yard-control',
          priority: 90,
          warehouseCode: 'REVO-YARD-01',
          operationType: 'gate-move',
        },
        {
          id: 'new-revo-yard-staging',
          label: 'New Revo Yard Staging',
          templateCode: 'yard-control',
          priority: 85,
          warehouseCode: 'REVO-STAGING-01',
          operationType: 'yard-staging',
        },
        {
          id: 'new-revo-slot-control',
          label: 'New Revo Slot Control',
          templateCode: 'yard-control',
          priority: 80,
          operationType: 'slot-control',
        },
        {
          id: 'new-revo-receiving-workflow',
          label: 'New Revo Receiving Workflow',
          templateCode: 'yard-control',
          priority: 78,
          operationType: 'receiving-to-putaway',
        },
        {
          id: 'new-revo-lot-control',
          label: 'New Revo Lot Control',
          templateCode: 'yard-control',
          priority: 76,
          operationType: 'lot-control',
        },
        {
          id: 'new-revo-shipping-workflow',
          label: 'New Revo Shipping Workflow',
          templateCode: 'yard-control',
          priority: 74,
          operationType: 'picking-to-shipping',
        },
      ],
    },
    validationContexts: [
      { label: 'Gate move', warehouseCode: 'REVO-YARD-01', operationType: 'gate-move', expectedTemplateCode: 'yard-control' },
      { label: 'Yard staging', warehouseCode: 'REVO-STAGING-01', operationType: 'yard-staging', expectedTemplateCode: 'yard-control' },
      { label: 'Slot control', operationType: 'slot-control', expectedTemplateCode: 'yard-control' },
      { label: 'Receiving workflow', operationType: 'receiving-to-putaway', expectedTemplateCode: 'yard-control' },
      { label: 'Lot control', operationType: 'lot-control', expectedTemplateCode: 'yard-control' },
      { label: 'Shipping workflow', operationType: 'picking-to-shipping', expectedTemplateCode: 'yard-control' },
    ],
    uatSmokePath: [
      {
        id: 'new-revo-gate-in',
        label: 'Gate in',
        route: '/wms/arrivals?warehouseCode=REVO-YARD-01&operationType=gate-move',
        context: { warehouseCode: 'REVO-YARD-01', operationType: 'gate-move' },
        expectedTemplateCode: 'yard-control',
      },
      {
        id: 'new-revo-yard-slot',
        label: 'Yard slot',
        route: '/wms/schedule?warehouseCode=REVO-STAGING-01&operationType=yard-staging',
        context: { warehouseCode: 'REVO-STAGING-01', operationType: 'yard-staging' },
        expectedTemplateCode: 'yard-control',
      },
      {
        id: 'new-revo-stuff-unstuff',
        label: 'Stuff or unstuff',
        route: '/wms/orders?operationType=receiving-to-putaway',
        context: { operationType: 'receiving-to-putaway' },
        expectedTemplateCode: 'yard-control',
      },
      {
        id: 'new-revo-lot-control',
        label: 'Lot control',
        route: '/wms/lots?operationType=lot-control',
        context: { operationType: 'lot-control' },
        expectedTemplateCode: 'yard-control',
      },
      {
        id: 'new-revo-gate-out',
        label: 'Gate out',
        route: '/wms/outbound?warehouseCode=REVO-YARD-01&operationType=picking-to-shipping',
        context: { warehouseCode: 'REVO-YARD-01', operationType: 'picking-to-shipping' },
        expectedTemplateCode: 'yard-control',
      },
    ],
  },
];

export const WMS_FOCUSED_TENANT_STARTER_CONFIG_INDEX: Record<WmsFocusedTenantStarterCode, WmsFocusedTenantStarterConfig> =
  Object.fromEntries(WMS_FOCUSED_TENANT_STARTER_CONFIGS.map((config) => [config.code, config])) as Record<WmsFocusedTenantStarterCode, WmsFocusedTenantStarterConfig>;

export function getWmsFocusedTenantStarterConfig(
  code: WmsFocusedTenantStarterCode,
): WmsFocusedTenantStarterConfig {
  return WMS_FOCUSED_TENANT_STARTER_CONFIG_INDEX[code];
}

export function buildWmsFocusedTenantUatSmokeMatrix(
  configs: WmsFocusedTenantStarterConfig[] = WMS_FOCUSED_TENANT_STARTER_CONFIGS,
): WmsFocusedTenantUatSmokeMatrixRow[] {
  return configs.map((config) => {
    const steps = config.uatSmokePath.map((step) => {
      const resolved = resolveWmsTemplateSelection(config.runtimeConfig, step.context);
      return {
        ...step,
        resolvedTemplateCode: resolved.templateCode,
        resolvedProfileKey: resolved.profileKey,
        source: resolved.source,
        matchedAssignmentId: resolved.matchedAssignment?.id ?? null,
        ready: resolved.templateCode === step.expectedTemplateCode,
      };
    });

    return {
      code: config.code,
      accountName: config.accountName,
      focusProfile: config.focusProfile,
      templateCode: config.templateCode,
      readyCount: steps.filter((step) => step.ready).length,
      totalCount: steps.length,
      steps,
    };
  });
}

export function isWmsTemplateCode(value: unknown): value is WmsTemplateCode {
  return typeof value === 'string' && value in WMS_PROFILE_TEMPLATES;
}

export function getWmsTemplate(code?: string | null): WmsProfileTemplate {
  const resolvedCode = resolveWmsTemplateCode(code);
  return WMS_PROFILE_TEMPLATES[resolvedCode];
}

export function resolveWmsTemplateCode(
  templateCode?: string | null,
  warehouseProfile?: string | null,
): WmsTemplateCode {
  if (isWmsTemplateCode(templateCode)) {
    return templateCode;
  }
  return PROFILE_TO_TEMPLATE_CODE[resolveProfileKey(warehouseProfile)] ?? 'hybrid-flex';
}

function readFirstSearchParam(
  params: WmsTemplateResolutionSearchParams,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = params.get(key)?.trim();
    if (value) return value;
  }
  return undefined;
}

export function buildWmsTemplateResolutionContextFromSearchParams(
  params: WmsTemplateResolutionSearchParams,
): WmsTemplateResolutionContext {
  return {
    explicitTemplateCode: readFirstSearchParam(params, [
      'explicitTemplateCode',
      'templateCode',
      'wmsTemplateCode',
      'wmTemplateCode',
      'wmPattern',
      'pattern',
    ]),
    warehouseCode: readFirstSearchParam(params, ['warehouseCode', 'warehouse', 'wh']),
    customerCode: readFirstSearchParam(params, ['customerCode', 'customer']),
    operationType: readFirstSearchParam(params, ['operationType', 'operation']),
    jobType: readFirstSearchParam(params, ['jobType', 'job']),
  };
}

function countSpecificity(rule: WmsTemplateAssignmentConfig): number {
  return ['warehouseCode', 'customerCode', 'operationType', 'jobType']
    .reduce((count, key) => (rule[key as keyof WmsTemplateAssignmentConfig] ? count + 1 : count), 0);
}

function getAssignmentResolutionTier(rule: WmsTemplateAssignmentConfig): number {
  const hasWarehouse = Boolean(rule.warehouseCode);
  const hasCustomer = Boolean(rule.customerCode);
  const hasOpOrJob = Boolean(rule.operationType || rule.jobType);

  if (hasWarehouse && hasCustomer) return 0;
  if (hasWarehouse) return 1;
  if (hasCustomer) return 2;
  if (hasOpOrJob) return 3;
  return 4;
}

function matchesAssignment(
  assignment: WmsTemplateAssignmentConfig,
  context?: WmsTemplateResolutionContext,
): boolean {
  if (assignment.enabled === false) return false;
  if (!isWmsTemplateCode(assignment.templateCode)) return false;

  if (assignment.warehouseCode && assignment.warehouseCode !== context?.warehouseCode) return false;
  if (assignment.customerCode && assignment.customerCode !== context?.customerCode) return false;
  if (assignment.operationType && assignment.operationType !== context?.operationType) return false;
  if (assignment.jobType && assignment.jobType !== context?.jobType) return false;

  return true;
}

export function sortWmsTemplateAssignments(
  assignments: WmsTemplateAssignmentConfig[] | null | undefined,
): WmsTemplateAssignmentConfig[] {
  return [...(assignments ?? [])].sort((left, right) => {
    const tierDelta = getAssignmentResolutionTier(left) - getAssignmentResolutionTier(right);
    if (tierDelta !== 0) return tierDelta;
    const priorityDelta = (right.priority ?? 0) - (left.priority ?? 0);
    if (priorityDelta !== 0) return priorityDelta;
    return countSpecificity(right) - countSpecificity(left);
  });
}

export function resolveWmsTemplateSelection(
  config: WmsTemplateRuntimeConfig,
  context?: WmsTemplateResolutionContext,
): ResolvedWmsTemplateSelection {
  const assignments = sortWmsTemplateAssignments(config.warehouseTemplateAssignments)
    .filter((assignment) => assignment.enabled !== false && isWmsTemplateCode(assignment.templateCode));

  if (isWmsTemplateCode(context?.explicitTemplateCode)) {
    const template = WMS_PROFILE_TEMPLATES[context.explicitTemplateCode];
    return {
      templateCode: context.explicitTemplateCode,
      template,
      profileKey: template.profileKey,
      profile: getWmsProfile(template.profileKey),
      source: 'explicit',
      matchedAssignment: null,
      assignments,
    };
  }

  const matchedAssignment = assignments.find((assignment) => matchesAssignment(assignment, context)) ?? null;

  if (matchedAssignment) {
    const template = WMS_PROFILE_TEMPLATES[matchedAssignment.templateCode];
    return {
      templateCode: matchedAssignment.templateCode,
      template,
      profileKey: template.profileKey,
      profile: getWmsProfile(template.profileKey),
      source: 'assignment',
      matchedAssignment,
      assignments,
    };
  }

  const source = isWmsTemplateCode(config.warehouseProfileTemplateCode) ? 'template-code' : 'profile-fallback';
  const templateCode = resolveWmsTemplateCode(config.warehouseProfileTemplateCode, config.warehouseProfile);
  const template = WMS_PROFILE_TEMPLATES[templateCode];

  return {
    templateCode,
    template,
    profileKey: template.profileKey,
    profile: getWmsProfile(template.profileKey),
    source,
    matchedAssignment: null,
    assignments,
  };
}

export function describeWmsTemplateAssignment(assignment: WmsTemplateAssignmentConfig): string {
  const parts = [
    assignment.warehouseCode ? `仓库:${assignment.warehouseCode}` : null,
    assignment.customerCode ? `客户:${assignment.customerCode}` : null,
    assignment.operationType ? `作业:${assignment.operationType}` : null,
    assignment.jobType ? `单据:${assignment.jobType}` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' / ') : '租户默认模板';
}

export function buildWmsRuntimePageContext(
  pageKey: string,
  selection: ResolvedWmsTemplateSelection,
): WmsRuntimePageContext {
  const bundle = selection.template.defaultRuntimeBundle;
  return {
    pageKey,
    templateCode: selection.templateCode,
    profileKey: selection.profileKey,
    templateLabel: selection.template.label,
    source: selection.source,
    bundleCode: bundle.code,
    semanticModelHint: bundle.semanticModelHint,
    workspaceTemplateHint: bundle.workspaceTemplateHint,
    reportModelHint: bundle.reportModelHint,
    billingModelHint: bundle.billingModelHint,
    matchedAssignmentLabel: selection.matchedAssignment
      ? describeWmsTemplateAssignment(selection.matchedAssignment)
      : null,
  };
}

function normalizeRuntimeKey(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function workspaceCandidateKeys(candidate: {
  id?: string;
  label?: string;
  bindingId?: string;
  content?: { entitySlug?: string; rendererKey?: string; resourcePath?: string; bindingId?: string };
  sections?: Array<{ content?: { entitySlug?: string; rendererKey?: string; resourcePath?: string; bindingId?: string } }>;
}): string[] {
  return [
    candidate.id,
    candidate.label,
    candidate.bindingId,
    candidate.content?.entitySlug,
    candidate.content?.rendererKey,
    candidate.content?.resourcePath,
    candidate.content?.bindingId,
    ...(candidate.sections ?? []).flatMap((section) => [
      section.content?.entitySlug,
      section.content?.rendererKey,
      section.content?.resourcePath,
      section.content?.bindingId,
    ]),
  ].map(normalizeRuntimeKey).filter(Boolean);
}

function findWorkspaceTabMatch<TTab extends WmsRuntimeWorkspaceTab>(
  tabs: TTab[],
  usedKeys: Set<string>,
  candidateKeys: string[],
): TTab | null {
  return tabs.find((tab) => {
    if (usedKeys.has(tab.key)) return false;
    const tabKeys = [normalizeRuntimeKey(tab.key), normalizeRuntimeKey(tab.label)];
    return tabKeys.some((tabKey) => candidateKeys.some((candidateKey) => candidateKey.includes(tabKey) || tabKey.includes(candidateKey)));
  }) ?? null;
}

export function applyWmsWorkspaceTemplateToTabs<TTab extends WmsRuntimeWorkspaceTab>(
  tabs: TTab[],
  workspace?: WmsRuntimeWorkspaceTemplate | null,
): WmsWorkspaceTabOverlayResult<TTab> {
  if (!workspace) {
    return { tabs, applied: false, appliedLabels: [] };
  }

  const orderedMatches: Array<{ tabKey: string; label: string }> = [];
  const usedKeys = new Set<string>();
  const candidates = [
    ...(workspace.boards ?? []).map((board) => ({ ...board, source: 'board' as const })),
    ...(workspace.queues ?? []).map((queue) => ({ ...queue, source: 'queue' as const })),
  ];

  for (const candidate of candidates) {
    const matchedTab = findWorkspaceTabMatch(tabs, usedKeys, workspaceCandidateKeys(candidate));
    if (!matchedTab) continue;
    usedKeys.add(matchedTab.key);
    orderedMatches.push({ tabKey: matchedTab.key, label: candidate.label });
  }

  if (orderedMatches.length === 0) {
    return { tabs, applied: false, appliedLabels: [] };
  }

  const matchByTabKey = new Map(orderedMatches.map((match) => [match.tabKey, match]));
  const renamedTabs = tabs.map((tab) => {
    const match = matchByTabKey.get(tab.key);
    return match ? { ...tab, label: match.label } : tab;
  });
  const tabByKey = new Map(renamedTabs.map((tab) => [tab.key, tab]));
  const matchedTabs = orderedMatches
    .map((match) => tabByKey.get(match.tabKey))
    .filter((tab): tab is TTab => Boolean(tab));
  const unmatchedTabs = renamedTabs.filter((tab) => !matchByTabKey.has(tab.key));

  return {
    tabs: [...matchedTabs, ...unmatchedTabs],
    applied: true,
    appliedLabels: orderedMatches.map((match) => match.label),
  };
}

function applyRuntimeFieldsToColumns<TTab extends WmsRuntimeColumnTab>(
  tabs: TTab[],
  runtimeFields: Array<{ field: string; label: string }>,
): WmsColumnOverlayResult<TTab> {
  if (runtimeFields.length === 0) {
    return { tabs, applied: false, appliedLabels: [] };
  }

  const runtimeByField = new Map(runtimeFields.map((field) => [normalizeRuntimeKey(field.field), field]));
  const appliedLabels: string[] = [];
  const nextTabs = tabs.map((tab) => {
    const matchedColumns: WmsRuntimeColumn[] = [];
    const unmatchedColumns: WmsRuntimeColumn[] = [];

    for (const column of tab.columns) {
      const runtimeField = runtimeByField.get(normalizeRuntimeKey(column.field));
      if (runtimeField) {
        matchedColumns.push({ ...column, label: runtimeField.label });
        appliedLabels.push(runtimeField.label);
      } else {
        unmatchedColumns.push(column);
      }
    }

    if (matchedColumns.length === 0) return tab;
    return { ...tab, columns: [...matchedColumns, ...unmatchedColumns] };
  });

  return {
    tabs: nextTabs,
    applied: appliedLabels.length > 0,
    appliedLabels,
  };
}

export function applyWmsReportModelToTabs<TTab extends WmsRuntimeColumnTab>(
  tabs: TTab[],
  reportModel?: WmsRuntimeReportModel | null,
): WmsColumnOverlayResult<TTab> {
  const runtimeFields = [
    ...(reportModel?.dimensions ?? []).map((dimension) => ({ field: dimension.field, label: dimension.label })),
    ...(reportModel?.measures ?? []).map((measure) => ({ field: measure.field, label: measure.label })),
  ];
  return applyRuntimeFieldsToColumns(tabs, runtimeFields);
}

export function applyWmsBillingModelToTabs<TTab extends WmsRuntimeColumnTab>(
  tabs: TTab[],
  billingModel?: WmsRuntimeBillingModel | null,
): WmsColumnOverlayResult<TTab> {
  if (!billingModel) {
    return { tabs, applied: false, appliedLabels: [] };
  }

  const familyById = new Map((billingModel.chargeFamilies ?? []).map((family) => [family.id, family]));
  const runtimeFields = (billingModel.ratingRules ?? []).flatMap((rule) => {
    const family = familyById.get(rule.familyId);
    return [
      rule.rateKey ? { field: rule.rateKey, label: `${family?.label ?? rule.familyId} Rate` } : null,
      family?.billableUnit ? { field: family.billableUnit, label: family.label } : null,
    ].filter((field): field is { field: string; label: string } => Boolean(field));
  });

  const columnResult = applyRuntimeFieldsToColumns(tabs, runtimeFields);
  const familyCandidates = billingModel.chargeFamilies ?? [];
  const usedKeys = new Set<string>();
  const tabMatches: Array<{ tabKey: string; label: string }> = [];

  for (const family of familyCandidates) {
    const candidateKeys = [family.id, family.code, family.label, family.billableUnit].map(normalizeRuntimeKey).filter(Boolean);
    const matchedTab = findWorkspaceTabMatch(columnResult.tabs, usedKeys, candidateKeys);
    if (!matchedTab) continue;
    usedKeys.add(matchedTab.key);
    tabMatches.push({ tabKey: matchedTab.key, label: family.label });
  }

  const matchByTabKey = new Map(tabMatches.map((match) => [match.tabKey, match]));
  const tabsWithLabels = columnResult.tabs.map((tab) => {
    const match = matchByTabKey.get(tab.key);
    return match ? { ...tab, label: match.label } : tab;
  });
  const appliedLabels = [...columnResult.appliedLabels, ...tabMatches.map((match) => match.label)];

  return {
    tabs: tabsWithLabels,
    applied: appliedLabels.length > 0,
    appliedLabels,
  };
}

export function serializeWmsTemplateConfigPatch(config: WmsTemplateRuntimeConfig): string {
  return JSON.stringify(config, null, 2);
}
